import { api, type ProgrammeModule } from "./api.ts";
import { h, icon } from "./dom.ts";

// Carnet de notes partagé entre le panneau et les boutons « Ajouter à mes notes ».
const notes = {
  content: "",
  loaded: null as Promise<void> | null,
  timer: 0,
  listeners: new Set<(state: string) => void>(),

  load(): Promise<void> {
    this.loaded ??= api.notes().then((n) => void (this.content = n.content));
    return this.loaded;
  },

  set(content: string): void {
    this.content = content;
    this.emit("Modifications en cours…");
    clearTimeout(this.timer);
    this.timer = window.setTimeout(() => {
      api.saveNotes(this.content).then(
        () => this.emit("Enregistré"),
        () => this.emit("Échec de l'enregistrement"),
      );
    }, 700);
  },

  emit(state: string): void {
    this.listeners.forEach((l) => l(state));
  },
};

export async function appendToNotes(text: string): Promise<void> {
  await notes.load();
  const separator = notes.content && !notes.content.endsWith("\n") ? "\n\n" : notes.content ? "\n" : "";
  notes.set(notes.content + separator + text + "\n");
  drawer?.open("notes");
}

type Tab = "cheatsheet" | "notes";

interface Drawer {
  element: HTMLElement;
  open(tab: Tab): void;
  toggle(tab: Tab): void;
  close(): void;
}

let drawer: Drawer | null = null;

/** Panneau latéral : aide-mémoire cumulatif et carnet de notes. */
export function createDrawer(modules: ProgrammeModule[], currentOrder: number): Drawer {
  let activeTab: Tab | null = null;

  // Aide-mémoire : modules déjà abordés et module courant, du plus récent au plus ancien.
  const sections = modules
    .filter((m) => m.available && m.order <= currentOrder && m.cheatsheet.length > 0)
    .sort((a, b) => b.order - a.order);
  const search = h("input", {
    type: "search",
    class: "drawer-search",
    placeholder: "Filtrer : chmod, groupe, droits…",
    "aria-label": "Filtrer l'aide-mémoire",
  });
  const cheatList = h(
    "div",
    { class: "cheat-list" },
    sections.map((m) =>
      h(
        "section",
        { class: "cheat-section" },
        h("h3", {}, `Module ${m.order} · ${m.title}`, m.order === currentOrder ? h("span", { class: "tag" }, "en cours") : null),
        h(
          "dl",
          {},
          m.cheatsheet.map((entry) =>
            h("div", { class: "cheat-entry" }, h("dt", {}, h("code", {}, entry.cmd)), h("dd", {}, entry.desc)),
          ),
        ),
      ),
    ),
    sections.length === 0 ? h("p", { class: "muted" }, "Les commandes vues apparaîtront ici au fil des modules.") : null,
  );
  search.addEventListener("input", () => {
    const q = search.value.trim().toLowerCase();
    cheatList.querySelectorAll<HTMLElement>(".cheat-section").forEach((section) => {
      let visible = 0;
      section.querySelectorAll<HTMLElement>(".cheat-entry").forEach((entry) => {
        const match = !q || entry.textContent!.toLowerCase().includes(q);
        entry.hidden = !match;
        if (match) visible++;
      });
      section.hidden = visible === 0;
    });
  });
  const cheatPanel = h("div", { class: "drawer-panel" }, search, cheatList);

  // Carnet de notes.
  const textarea = h("textarea", {
    class: "notes-area",
    placeholder: "Vos notes personnelles : commandes à retenir, explications avec vos propres mots…",
    "aria-label": "Mes notes",
    spellcheck: "true",
  });
  const status = h("span", { class: "notes-status muted" });
  notes.listeners.add((state) => {
    status.textContent = state;
    if (document.activeElement !== textarea) textarea.value = notes.content;
  });
  textarea.addEventListener("input", () => notes.set(textarea.value));
  const notesPanel = h(
    "div",
    { class: "drawer-panel" },
    h("p", { class: "muted small" }, "Enregistrées automatiquement et conservées d'un module à l'autre."),
    textarea,
    status,
  );

  const tabButtons = {
    cheatsheet: h("button", { class: "drawer-tab", onclick: () => show("cheatsheet") }, icon("list", 16), "Aide-mémoire"),
    notes: h("button", { class: "drawer-tab", onclick: () => show("notes") }, icon("note", 16), "Mes notes"),
  };
  const body = h("div", { class: "drawer-body" });
  const element = h(
    "aside",
    { class: "drawer", "aria-label": "Panneau d'aide", hidden: true },
    h(
      "header",
      { class: "drawer-header" },
      h("div", { class: "drawer-tabs", role: "tablist" }, tabButtons.cheatsheet, tabButtons.notes),
      h("button", { class: "icon-btn", "aria-label": "Fermer le panneau", onclick: () => api_.close() }, icon("x")),
    ),
    body,
  );

  function show(tab: Tab): void {
    activeTab = tab;
    element.hidden = false;
    body.replaceChildren(tab === "cheatsheet" ? cheatPanel : notesPanel);
    for (const [name, btn] of Object.entries(tabButtons)) btn.setAttribute("aria-selected", String(name === tab));
    if (tab === "notes") {
      void notes.load().then(() => {
        textarea.value = notes.content;
        textarea.focus();
        textarea.setSelectionRange(textarea.value.length, textarea.value.length);
        textarea.scrollTop = textarea.scrollHeight;
      });
    } else {
      search.focus();
    }
  }

  const api_: Drawer = {
    element,
    open: show,
    toggle: (tab) => (activeTab === tab && !element.hidden ? api_.close() : show(tab)),
    close: () => {
      element.hidden = true;
      activeTab = null;
    },
  };
  element.addEventListener("keydown", (e) => e.key === "Escape" && api_.close());
  drawer = api_;
  return api_;
}
